import React, { useState, useEffect } from 'react';
import { Activity, Plus, Trash2, Loader2 } from 'lucide-react';

export default function AdminTestSessions() {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ title: '', type: 'Fintech', reward: '15,00 €', duration: '20 Min.', deadline: '2026-12-31' });

  useEffect(() => { fetchSessions(); }, []);

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/test-sessions', { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setSessions(await response.json());
    } catch (err) { console.error(err); } finally { setIsLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/test-sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        fetchSessions();
        setIsModalOpen(false);
        setFormData({ title: '', type: 'Fintech', reward: '15,00 €', duration: '20 Min.', deadline: '2026-12-31' });
      }
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm("Auftrag wirklich löschen? Er verschwindet dann bei allen Testern.")) return;
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/admin/test-sessions/${sessionId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setSessions(prev => prev.filter(s => s.id !== sessionId));
    } catch (err) { console.error(err); }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-primary">Test-Sessions & Aufträge</h1>
          <p className="text-muted-foreground">Erstellen und verwalten Sie hier die Aufträge für Ihre Tester.</p>
        </div>
        <button onClick={() => setIsModalOpen(true)} className="bg-accent text-white px-4 py-2.5 rounded-md font-medium hover:bg-accent/90 transition-colors flex items-center gap-2">
          <Plus className="w-5 h-5" /> Neuer Auftrag
        </button>
      </div>

      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden w-full">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex justify-center h-64 items-center"><Loader2 className="w-8 h-8 animate-spin text-accent" /></div>
          ) : sessions.length === 0 ? (
            <div className="flex justify-center h-64 items-center text-muted-foreground">Noch keine Aufträge angelegt.</div>
          ) : (
            <table className="w-full text-left border-collapse min-w-[700px]">
              <thead>
                <tr className="bg-slate-50 border-b border-border text-sm text-secondary">
                  <th className="p-4 font-semibold">Session & ID</th>
                  <th className="p-4 font-semibold">Branche</th>
                  <th className="p-4 font-semibold">Dauer / Vergütung</th>
                  <th className="p-4 font-semibold">Status</th>
                  <th className="p-4 font-semibold text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => (
                  <tr key={session.id} className="border-b border-border hover:bg-slate-50/50">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center text-accent"><Activity className="w-5 h-5" /></div>
                        <div><p className="font-medium text-primary">{session.title}</p><p className="text-xs text-muted-foreground font-mono">{session.id}</p></div>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-secondary">{session.type}</td>
                    <td className="p-4 text-sm text-secondary">{session.duration} <br/><span className="text-green-600 font-medium">{session.reward}</span></td>
                    <td className="p-4"><span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Aktiv</span></td>
                    <td className="p-4 text-right">
                      <button onClick={() => handleDelete(session.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-md" title="Löschen"><Trash2 className="w-5 h-5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-4 border-b border-border"><h2 className="font-bold text-lg text-primary">Neuen Auftrag erstellen</h2></div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Auftrags-Titel</label>
                <input required type="text" className="w-full p-2 border border-border rounded" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="z.B. UX Test Banking App" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Branche</label>
                  <input required type="text" className="w-full p-2 border border-border rounded" value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Vergütung</label>
                  <input required type="text" className="w-full p-2 border border-border rounded" value={formData.reward} onChange={e => setFormData({...formData, reward: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Geschätzte Dauer</label>
                  <input required type="text" className="w-full p-2 border border-border rounded" value={formData.duration} onChange={e => setFormData({...formData, duration: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Deadline</label>
                  <input required type="date" className="w-full p-2 border border-border rounded" value={formData.deadline} onChange={e => setFormData({...formData, deadline: e.target.value})} />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded font-medium">Abbrechen</button>
                <button type="submit" className="px-4 py-2 text-white bg-accent hover:bg-accent/90 rounded font-medium">Speichern & Veröffentlichen</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}